import { useState, useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { getCollectionStatus, type CollectionState } from '../../services/api'

/**
 * The collection status badge — AB #113227.
 *
 * WHAT IT REPLACES. Two lines of static markup: a `bg-success` dot and the words "All
 * systems collecting", with no props, no query and no state. It said exactly that on
 * 2026-09-11, 12, 13 and 14 while the nightly scan did not run at all and the Compliance
 * hub sat at NOT COLLECTED. A badge that cannot be wrong is not reassurance, it is
 * decoration — and it was read as reassurance for four days.
 *
 * THE RULE: unknown is never green. A missing record, an unreadable timestamp or a failed
 * request all render grey. The same "absent is not empty" discipline the rest of the
 * platform follows — a surface must never claim health it cannot evidence.
 *
 * Scope comes from the server, not from here. Internal principals get fleet state;
 * customers get their own tenants' freshness and nothing about the fleet. This component
 * renders whichever it is handed and never decides which one it is entitled to.
 */

const DOT: Record<CollectionState, string> = {
  collecting: 'bg-success',
  degraded: 'bg-warning',
  stalled: 'bg-danger',
  unknown: 'bg-text-muted',
}

const TEXT: Record<CollectionState, string> = {
  collecting: 'text-text-muted',
  degraded: 'text-warning',
  stalled: 'text-danger',
  unknown: 'text-text-muted',
}

/** Compact age for the badge itself; the panel carries the full sentence. */
function shortAge(hours: number | null): string {
  if (hours === null) return ''
  if (hours < 1) return '<1h'
  if (hours < 48) return `${hours}h`
  return `${Math.floor(hours / 24)}d`
}

const fmt = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—'

export default function CollectionBadge() {
  const [open, setOpen] = useState(false)
  const wrap = useRef<HTMLDivElement>(null)

  const q = useQuery({
    queryKey: ['collection-status'],
    queryFn: getCollectionStatus,
    // A minute is plenty: the underlying fact changes at most once a night. Polling any
    // harder would add load to every page for no extra truth.
    refetchInterval: 60_000,
    staleTime: 30_000,
  })

  // Close on an outside click. A popover that traps the pointer in a global header is the
  // kind of small rudeness people remember.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  // While loading, and if the call fails, the state is UNKNOWN — never the previous green.
  const s = q.data
  const state: CollectionState = q.isError || !s ? 'unknown' : s.state
  const label = q.isLoading
    ? 'Checking collection…'
    : q.isError || !s
      ? 'Collection status unknown'
      : s.label
  const age = state === 'stalled' && s?.ageHours != null ? ` · ${shortAge(s.ageHours)}` : ''

  return (
    <div className="relative hidden md:block" ref={wrap}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={`${label}. Open collection detail`}
        title={s?.detail ?? label}
        className={clsx(
          'flex items-center gap-2 text-xs bg-surface border border-border px-2.5 py-1.5',
          'rounded-lg whitespace-nowrap hover:border-text-muted transition-colors',
          TEXT[state],
        )}
      >
        <span
          className={clsx(
            'w-[7px] h-[7px] rounded-full',
            DOT[state],
            s?.running && 'animate-pulse',
          )}
        />
        {label}{age}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[22rem] card p-4 z-50 shadow-lg">
          <div className="text-sm font-semibold text-text-primary mb-1">{label}</div>
          <p className="text-xs text-text-muted mb-3">{s?.detail ?? 'No status available.'}</p>

          {s?.scope === 'fleet' && s.lastRun && (
            <div className="space-y-2 text-xs">
              <Row k="Last run" v={`${fmt(s.lastRun.finishedAt ?? s.lastRun.startedAt)} (${s.lastRun.trigger})`} />
              <Row k="Tenants" v={String(s.lastRun.tenants)} />
              {s.lastRun.skipped.length > 0 && (
                <Detail title={`Skipped (${s.lastRun.skipped.length})`} tone="text-warning">
                  {s.lastRun.skipped.map((x, i) => (
                    <li key={i}><b>{x.tenant}</b> · {x.step} — {x.reason ?? x.message}</li>
                  ))}
                </Detail>
              )}
              {s.lastRun.errors.length > 0 && (
                <Detail title={`Errors (${s.lastRun.errors.length})`} tone="text-danger">
                  {s.lastRun.errors.map((x, i) => (
                    <li key={i}><b>{x.tenant}</b> · {x.step} — {x.message ?? x.reason}</li>
                  ))}
                </Detail>
              )}
            </div>
          )}

          {s?.scope === 'tenant' && s.tenants && (
            <ul className="space-y-1 text-xs">
              {s.tenants.map((t) => (
                <li key={t.tenantId} className="flex items-baseline justify-between gap-3">
                  <span className="text-text-primary truncate" title={t.tenantName}>{t.tenantName}</span>
                  <span className={clsx('whitespace-nowrap', t.lastCollectedAt ? 'text-text-muted' : 'text-warning')}>
                    {/* Never collected is its own state, not a zero. A tenant onboarded an
                        hour ago has no data yet; that is not the same as data going stale. */}
                    {t.lastCollectedAt ? fmt(t.lastCollectedAt) : 'never collected'}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {state === 'unknown' && (
            <p className="text-xs text-text-muted">
              Nothing has reported in. This is not the same as everything being healthy.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-text-muted">{k}</span>
      <span className="text-text-primary text-right">{v}</span>
    </div>
  )
}

function Detail({ title, tone, children }: { title: string; tone: string; children: React.ReactNode }) {
  return (
    <div>
      <div className={clsx('font-semibold mb-1', tone)}>{title}</div>
      <ul className="space-y-1 text-text-muted max-h-40 overflow-y-auto">{children}</ul>
    </div>
  )
}
